import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Anchor, Waves, Sparkles, Flame } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";

const STAGES = [
    {
        label: "Pour",
        body: "Oils blended and poured into loaf molds.",
        status: "Complete",
    },
    {
        label: "Rest",
        body: "Bars resting on volcanic shelving — 4 to 6 weeks.",
        status: "In Progress",
    },
    {
        label: "Cut",
        body: "Hand-cut, wrapped, and filmed for the holiday drop.",
        status: "Pending",
    },
    {
        label: "Ship",
        body: "Holiday 2026 release · subscribers first.",
        status: "Pending",
    },
];

export default function ComingSoon() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) {
            toast.error("Please enter a valid email.");
            return;
        }
        setLoading(true);
        try {
            await subscribeNewsletter(email);
            toast.success("Logged. The first loaf off the curing rack is yours.");
            setEmail("");
        } catch {
            toast.error("That didn't go through. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative bg-forest text-cream min-h-screen overflow-hidden"
            data-testid="coming-soon-page"
        >
            <div className="grain absolute inset-0 opacity-50" />

            {/* Ambient aurora */}
            <div
                className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-30"
                style={{ background: "var(--ig-gold)" }}
                aria-hidden
            />
            <div
                className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
                style={{ background: "var(--ig-olive)" }}
                aria-hidden
            />

            <section className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
                <div className="grid md:grid-cols-12 gap-12 items-center">
                    {/* Copy */}
                    <div className="md:col-span-7 rise-in">
                        <div className="divider-rule text-gold">
                            <Anchor size={12} /> Curing Phase · Holiday 2026
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.98] mt-8">
                            The batch is
                            <br />
                            <span className="italic text-gold">curing.</span>
                        </h1>
                        <p className="mt-8 text-cream/75 text-base md:text-lg leading-relaxed max-w-xl">
                            Each bar rests on volcanic shelves, quiet as a
                            North Shore sunrise. Every loaf earns its wrapper
                            the slow way — patient rest before it reaches
                            you.
                        </p>
                        <p className="mt-4 text-cream/60 text-sm md:text-base leading-relaxed max-w-xl">
                            We're launching{" "}
                            <span className="text-gold font-medium">
                                Holiday 2026
                            </span>
                            . In the meantime, this storefront is a placeholder
                            while we bring in curated companions.
                        </p>

                        <form
                            onSubmit={onSubmit}
                            className="mt-10 flex items-center border-b border-cream/30 pb-2 max-w-md"
                            data-testid="coming-soon-form"
                        >
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-cream placeholder:text-cream/40 py-2"
                                data-testid="coming-soon-email"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="text-[0.7rem] tracking-[0.3em] uppercase text-gold hover:text-cream transition-colors"
                                data-testid="coming-soon-submit"
                            >
                                {loading ? "…" : "Notify me →"}
                            </button>
                        </form>

                        <div className="mt-12 flex flex-wrap gap-4 items-center">
                            <Link
                                to="/craft"
                                className="btn-pill btn-gold"
                                data-testid="coming-soon-craft-btn"
                            >
                                <Flame size={14} /> Visit the Studio
                            </Link>
                            <Link
                                to="/about"
                                className="text-[0.72rem] tracking-[0.3em] uppercase text-cream/80 hover:text-gold transition-colors inline-flex items-center gap-2"
                                data-testid="coming-soon-about-link"
                            >
                                Our story
                            </Link>
                        </div>
                    </div>

                    {/* Curing timeline card */}
                    <aside
                        className="md:col-span-5 relative p-8 bg-forest-deep/70 border border-gold/30 rounded-sm backdrop-blur-sm"
                        data-testid="curing-timeline"
                    >
                        <div className="absolute -top-3 left-6 bg-gold text-forest text-[0.6rem] tracking-[0.35em] uppercase px-3 py-1 rounded-full flex items-center gap-1.5">
                            <Sparkles size={10} /> Studio Log
                        </div>
                        <h3 className="font-serif text-2xl mt-3">
                            Cure Status
                        </h3>
                        <ul className="mt-6 space-y-5">
                            {STAGES.map((s, i) => {
                                const done = s.status === "Complete";
                                const active = s.status === "In Progress";
                                return (
                                    <li
                                        key={s.label}
                                        className="flex gap-4"
                                        data-testid={`cure-stage-${i}`}
                                    >
                                        <div
                                            className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-serif text-sm border ${
                                                done
                                                    ? "bg-gold text-forest border-gold"
                                                    : active
                                                      ? "bg-transparent border-gold text-gold animate-pulse"
                                                      : "bg-transparent border-cream/25 text-cream/40"
                                            }`}
                                        >
                                            {i + 1}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-baseline justify-between gap-3">
                                                <h4 className="font-serif text-lg">
                                                    {s.label}
                                                </h4>
                                                <span
                                                    className={`text-[0.6rem] tracking-[0.3em] uppercase ${
                                                        done
                                                            ? "text-gold"
                                                            : active
                                                              ? "text-gold/80"
                                                              : "text-cream/40"
                                                    }`}
                                                >
                                                    {s.status}
                                                </span>
                                            </div>
                                            <p className="text-sm text-cream/65 mt-1 leading-relaxed">
                                                {s.body}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="mt-8 pt-6 border-t border-cream/15 text-[0.65rem] tracking-[0.3em] uppercase text-cream/50 flex items-center gap-2">
                            <Waves size={12} className="text-gold" />{" "}
                            Oʻahu · Hawaiʻi
                        </div>
                    </aside>
                </div>

                <div className="mt-20 grid sm:grid-cols-3 gap-4 text-center">
                    {[
                        { h: "Coming Soon", p: "First drop" },
                        { h: "Holiday 2026", p: "Launch date" },
                        { h: "10% CyberKids", p: "Gift toggle at launch" },
                    ].map((f) => (
                        <div
                            key={f.h}
                            className="p-6 border border-cream/15 rounded-sm"
                        >
                            <p className="font-serif text-2xl text-gold">
                                {f.h}
                            </p>
                            <p className="text-xs tracking-[0.25em] uppercase text-cream/60 mt-2">
                                {f.p}
                            </p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
